import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  PhotoIcon,
  HeartIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { dashboardAPI } from '../../services/api';
import { resolveImageUrl } from '../../utils/image';
// import {useLocation} from '../../hooks/useLocation'

interface PortfolioUpdate {
  id: string;
  url: string;
  thumbnail_url?: string;
  thumbnail_url_signed?: string;
  title: string;
  type: string;
  created_at: string;
  like_count: number;
  view_count: number;
  engagement_rate: number;
}

interface RecentPortfolioUpdatesProps {
    portfolioUpdates: PortfolioUpdate[];
}

const RecentPortfolioUpdates: React.FC<RecentPortfolioUpdatesProps> = ({portfolioUpdates}) => {
  const [updates, setUpdates] = useState<PortfolioUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation()
  

  // console.log(portfolioUpdates)
  useEffect(() => {
    if (portfolioUpdates && portfolioUpdates.length > 0) {
        setUpdates(portfolioUpdates);
        setLoading(false);
    } else if (!portfolioUpdates) {
        fetchPortfolioUpdates();
    }
  }, [portfolioUpdates]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const fetchPortfolioUpdates = async () => {
    try {
      const response = await dashboardAPI.getRecentPortfolioUpdates();
      setUpdates(response.data.data);
    } catch (error) {
      console.error('Failed to fetch portfolio updates:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 dark:bg-gray-900 dark:border-gray-800">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3 dark:bg-gray-700"></div>
          <div className="grid grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-lg dark:bg-gray-700"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-200 dark:bg-gray-900 dark:border-gray-800"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">Recent Portfolio Updates</h2>
        <Link
          to="/profile#portfolio"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium dark:text-teal-400 dark:hover:text-teal-300"
        >
          Manage Portfolio →
        </Link>
      </div>
      
      {updates.length > 0 ? (
        <div className="grid grid-cols-6 gap-4">
          {updates.map((update, index) => (
            <motion.div
              key={update.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 hover:shadow-lg transition-all duration-300 dark:bg-gray-800"
            >
              {/* Image */}
              <img
                src={resolveImageUrl(update.thumbnail_url_signed || update.url)}
                alt={update.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center">
                  <PhotoIcon className="h-6 w-6 mx-auto mb-1" />
                  <p className="text-xs font-medium truncate px-1">{update.title}</p>
                </div>
              </div>
              
              {/* Performance Badge */}
              {update.engagement_rate > 15 && (
                <div className="absolute top-2 right-2">
                  <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full p-1">
                    <ArrowTrendingUpIcon className="h-3 w-3" />
                  </div>
                </div>
              )}
              
              {/* Stats Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center justify-between text-white text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <HeartIconSolid className="h-3 w-3 text-red-400" />
                      <span>{update.like_count}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <EyeIcon className="h-3 w-3" />
                      <span>{update.view_count}</span>
                    </div>
                  </div>
                  <span className="text-gray-300">
                    {formatDistanceToNow(new Date(update.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <PhotoIcon className="h-12 w-12 text-gray-300 mx-auto mb-3 dark:text-gray-600" />
          <p className="text-gray-500 text-sm dark:text-gray-400">No recent portfolio updates</p>
          <p className="text-gray-400 text-xs mt-1 dark:text-gray-500">
            Upload photos to showcase your work
          </p>
          <Link 
            to="/portfolio/upload"
            className="inline-block mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium dark:text-teal-400 dark:hover:text-teal-300"
          >
            Upload Photos →
          </Link>
        </div>
      )}
      
      {/* Portfolio Stats */}
      {updates.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-base md:text-lg font-bold text-teal-600 dark:text-teal-400">
                {updates.reduce((sum, update) => sum + update.like_count, 0)}
              </div>
              <div className="text-[10px] md:text-xs text-gray-600 dark:text-gray-400">Total Likes</div>
            </div>
            
            <div>
              <div className="text-base md:text-lg font-bold text-orange-600 dark:text-orange-400">
                {updates.reduce((sum, update) => sum + update.view_count, 0)}
              </div>
              <div className="text-[10px] md:text-xs text-gray-600 dark:text-gray-400">Total Views</div>
            </div>
            
            <div>
              <div className="text-base md:text-lg font-bold text-purple-600 dark:text-purple-400">
                {(updates.reduce((sum, update) => sum + update.engagement_rate, 0) / updates.length).toFixed(1)}%
              </div>
              <div className="text-[10px] md:text-xs text-gray-600 dark:text-gray-400">Avg Engagement</div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RecentPortfolioUpdates;
